import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const HelpSupportModal = ({ isOpen, onClose, onSelectQuestion }) => {
    const navigate = useNavigate();
    const [activeIndex, setActiveIndex] = useState(null);

    if (!isOpen) return null;

    const faqs = [
        {
            q: "What can CarFido help with?",
            a: "CarFido is your personal AI car expert. It can diagnose dashboard warning lights, walk you through DIY repairs, explain maintenance schedules, translate mechanic quotes, and organize your vehicle's Glove Box files."
        },
        {
            q: "How accurate are CarFido diagnostics?",
            a: "CarFido leverages state-of-the-art diagnostic models trained on millions of repair records. However, for critical safety issues, we always recommend consulting a certified professional."
        },
        {
            q: "Can I upload photos or videos?",
            a: "Yes! You can take a photo of a warning light, a weird engine leak, or upload a video of a strange noise. Click the attachment icon in the chat bar to upload up to 10 files."
        },
        {
            q: "Can CarFido help determine if my car is safe to drive?",
            a: "Yes, CarFido analyzes your symptoms and warning lights to assess safety risks. It will explicitly advise you if a condition is highly hazardous and requires immediate attention."
        },
        {
            q: "Where are my reports and diagnostics saved?",
            a: "All diagnostics and work plans are stored under the Glove Box and Garage sections. If you link your phone number, they are securely synced across all your devices."
        },
        {
            q: "Is my vehicle data private?",
            a: "Absolutely. Your data is fully encrypted and never shared. You can view or delete your history at any time through the Settings page."
        }
    ];

    const exampleQuestions = [
        "Why is my Jeep shaking at highway speeds?",
        "Can I still drive with a misfire?",
        "Estimate the cost to replace my alternator.",
        "How do I diagnose a bad wheel bearing?",
        "What should I check first if my car won't start?"
    ];

    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const handleSupportClick = (path) => {
        navigate(path);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm transition-all duration-300 overflow-y-auto">
            {/* Modal Card */}
            <div className="relative w-full max-w-lg bg-[#F9F9F9] rounded-[24px] border border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.15)] flex flex-col my-8 overflow-hidden max-h-[85vh]">
                {/* Header */}
                <div className="sticky top-0 bg-[#F9F9F9] z-10 flex items-center justify-between px-6 py-5 border-b border-black/5">
                    <div className="flex items-center gap-2.5">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F9F9F9] text-[#676767]">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                <path d="M12 17h.01" />
                            </svg>
                        </div>
                        <span className="font-extrabold text-lg text-black tracking-tight">Help & Support</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="flex items-center justify-center w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 text-black/55 transition-colors"
                        aria-label="Close help modal"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* QUICK FAQS */}
                    <div className="space-y-3">
                        <h3 className="text-[10px] font-black uppercase tracking-[0.18em] text-[#D81F26]">Quick FAQs</h3>
                        <div className="space-y-2 bg-white rounded-2xl border border-black/5 p-2.5 shadow-sm">
                            {faqs.map((faq, index) => {
                                const isExpanded = activeIndex === index;
                                return (
                                    <div key={index} className={`border-b border-black/5 last:border-b-0 pb-2.5 last:pb-0 pt-2.5 first:pt-0`}>
                                        <button
                                            onClick={() => toggleAccordion(index)}
                                            className="w-full flex items-center justify-between text-left group"
                                        >
                                            <span className="text-[13px] font-bold text-black/80 group-hover:text-black transition-colors pr-4">
                                                {faq.q}
                                            </span>
                                            <svg
                                                className={`w-4 h-4 text-black/30 group-hover:text-black/60 transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        </button>
                                        <div
                                            className={`grid transition-all duration-200 ease-in-out ${isExpanded ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0 overflow-hidden'}`}
                                        >
                                            <div className="overflow-hidden">
                                                <p className="text-[12px] leading-relaxed text-black/65 font-medium pl-1 pr-2">
                                                    {faq.a}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* EXAMPLE QUESTIONS TO ASK DASH */}
                    <div className="space-y-3">
                        <h3 className="text-[10px] font-black uppercase tracking-[0.18em] text-[#D81F26]">Example Questions to Ask CarFido</h3>
                        <div className="space-y-1.5 bg-white rounded-2xl border border-black/5 p-2 shadow-sm">
                            {exampleQuestions.map((q, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => onSelectQuestion(q)}
                                    className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-[#F9F9F9]/60 transition-colors rounded-xl group border border-transparent hover:border-[#E6E6E6]"
                                >
                                    <svg
                                        className="w-4 h-4 text-[#8C8C8C] group-hover:text-[#D81F26] transition-colors shrink-0"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                    </svg>
                                    <span className="text-[12.5px] font-semibold text-black/75 group-hover:text-black transition-colors italic">
                                        "{q}"
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* SUPPORT */}
                    <div className="space-y-3">
                        <h3 className="text-[10px] font-black uppercase tracking-[0.18em] text-[#D81F26]">Support</h3>
                        <div className="space-y-2">
                            <button
                                onClick={() => handleSupportClick('/contact')}
                                className="w-full flex items-center justify-between px-4 py-3.5 bg-white hover:bg-neutral-50 border border-black/5 hover:border-black/10 rounded-xl transition-all shadow-sm group"
                            >
                                <span className="text-[13px] font-extrabold text-black/75 group-hover:text-black">
                                    Contact Support
                                </span>
                                <svg
                                    className="w-4 h-4 text-black/35 group-hover:text-black/75 transition-colors"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    <polyline points="15 3 21 3 21 9" />
                                    <line x1="10" y1="14" x2="21" y2="3" />
                                </svg>
                            </button>

                            {/* <button
                                onClick={() => handleSupportClick('/contact')}
                                className="w-full flex items-center justify-between px-4 py-3.5 bg-white hover:bg-neutral-50 border border-black/5 hover:border-black/10 rounded-xl transition-all shadow-sm group"
                            >
                                <span className="text-[13px] font-extrabold text-black/75 group-hover:text-black">
                                    Report a Bug
                                </span>
                                <svg
                                    className="w-4 h-4 text-black/35 group-hover:text-black/75 transition-colors"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    <polyline points="15 3 21 3 21 9" />
                                    <line x1="10" y1="14" x2="21" y2="3" />
                                </svg>
                            </button> */}

                            <button
                                onClick={() => handleSupportClick('/blogs')}
                                className="w-full flex items-center justify-between px-4 py-3.5 bg-white hover:bg-neutral-50 border border-black/5 hover:border-black/10 rounded-xl transition-all shadow-sm group"
                            >
                                <span className="text-[13px] font-extrabold text-black/75 group-hover:text-black">
                                    Visit Blog
                                </span>
                                <svg
                                    className="w-4 h-4 text-black/35 group-hover:text-black/75 transition-colors"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    <polyline points="15 3 21 3 21 9" />
                                    <line x1="10" y1="14" x2="21" y2="3" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HelpSupportModal;
